import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

/**
 * [FRAME-METER 2026-08-14] A measurement tool, not a feature — press Alt+Shift+F to show it.
 *
 * It exists because the 4K / high-refresh work must not be guessed at. The lesson from the
 * tab-switch investigation is on record: two perf "fixes" shipped on plausible reasoning and
 * neither was the cause; a ten-minute measurement found the real one. So before any CSS changes,
 * this gives a real before/after from the machine that actually matters — the user's.
 *
 * WHAT IT REPORTS, and why each line is here:
 *   FPS        — frames actually delivered over the last second.
 *   worst      — the slowest single frame in that second. This is the number that matters: an
 *                average of 118fps with one 40ms frame still reads as a visible stutter.
 *   budget     — 1000 / refresh. At 60Hz that is 16.7ms, at 120Hz only 8.3ms. Frames over budget
 *                are counted, because "how often do we miss" is the real smoothness question.
 *   refresh    — the display's rate, inferred from the FASTEST frame observed (the shortest gap
 *                the browser could deliver). Inferred, not queried: the web has no API for it.
 *   DPR / css  — devicePixelRatio and CSS viewport. These answer the "what is this machine set
 *                to?" question directly — DPR 1 at 3840px wide means 4K at 100% Windows scaling
 *                (everything sharp but tiny); DPR 1.5 at 2560 means 150%.
 *
 * Deliberately cheap: one rAF loop that only reads timestamps, and it renders at most twice a
 * second. It is inert until toggled, so it costs nothing in normal use.
 */

const Box = styled.div`
  position: fixed;
  right: 12px;
  bottom: 92px;
  z-index: 99999;
  padding: 10px 12px;
  border-radius: 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11.5px;
  line-height: 1.55;
  letter-spacing: 0.2px;
  color: #e6fff4;
  background: rgba(6, 20, 16, 0.92);
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.45)' : 'rgba(31, 216, 164, 0.45)')};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  pointer-events: none;
  white-space: pre;
  /* No backdrop-filter here on purpose: the thing measuring the cost of blur must not add any. */
`;

const Val = styled.span<{ $bad?: boolean; $warn?: boolean }>`
  color: ${(p) => (p.$bad ? '#fca5a5' : p.$warn ? '#fcd34d' : '#65e7bf')};
  font-weight: 700;
`;

interface Stats {
  fps: number;
  worst: number;
  over: number;
  refresh: number;
  dpr: number;
  vw: number;
  vh: number;
}

const FrameMeter: React.FC = () => {
  const [on, setOn] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);

  // Alt+Shift+F. Guarded against typing: this app is full of text inputs, so a bare letter key
  // (the PM board uses `d`) would fire mid-edit.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || !e.shiftKey) return;
      if (String(e.key).toLowerCase() !== 'f') return;
      const el = document.activeElement as HTMLElement | null;
      const tag = (el?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable) return;
      e.preventDefault();
      setOn((v) => !v);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!on) { setStats(null); return undefined; }
    let raf = 0;
    let last = performance.now();
    let windowStart = last;
    let frames = 0;
    let worst = 0;
    let over = 0;
    // Fastest gap ever seen → the display's refresh. Kept across the whole session so a single
    // slow patch cannot make a 120Hz screen look like 60Hz.
    let fastest = Infinity;

    const tick = (now: number) => {
      const dt = now - last;
      last = now;
      frames += 1;
      if (dt > worst) worst = dt;
      if (dt > 0.5 && dt < fastest) fastest = dt;

      const refresh = fastest < Infinity ? Math.round(1000 / fastest) : 60;
      const budget = 1000 / Math.max(refresh, 1);
      if (dt > budget * 1.5) over += 1;

      if (now - windowStart >= 1000) {
        setStats({
          fps: Math.round((frames * 1000) / (now - windowStart)),
          worst: Math.round(worst * 10) / 10,
          over,
          refresh,
          dpr: Math.round((window.devicePixelRatio || 1) * 100) / 100,
          vw: Math.round(window.innerWidth),
          vh: Math.round(window.innerHeight),
        });
        windowStart = now; frames = 0; worst = 0; over = 0;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [on]);

  if (!on) return null;
  const s = stats;
  const budget = s ? Math.round((1000 / Math.max(s.refresh, 1)) * 10) / 10 : 0;

  return (
    <Box role="status" aria-live="off">
      {s ? (
        <>
          {'fps     '}<Val $bad={s.fps < s.refresh * 0.7} $warn={s.fps < s.refresh * 0.92}>{s.fps}</Val>{` / ${s.refresh}\n`}
          {'worst   '}<Val $bad={s.worst > budget * 2} $warn={s.worst > budget}>{s.worst}</Val>{`ms  (budget ${budget}ms)\n`}
          {'over    '}<Val $bad={s.over > 5} $warn={s.over > 0}>{s.over}</Val>{' frame(s) last sec\n'}
          {`DPR     ${s.dpr}\n`}
          {`css px  ${s.vw}x${s.vh}\n`}
          {`device  ${Math.round(s.vw * s.dpr)}x${Math.round(s.vh * s.dpr)}`}
        </>
      ) : 'measuring…'}
    </Box>
  );
};

export default FrameMeter;
